import { apiTimeTracking } from "@/api/time-tracking.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook";
import { ApiErrorException } from "@/types/api.types";
import {
  ApiTimeEntrySaveInput,
  ApiTimeEntryFilter,
  ApiTimesheetFilter,
} from "@/types/time-tracking.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const timeTrackingKeys = createQueryKeys("timeTracking", {
  status: {
    queryKey: ["status"],
    queryFn: async () => {
      const result = await apiTimeTracking.getStatus();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  },
  timesheets: (filter: ApiTimesheetFilter) => ({
    queryKey: [filter],
    queryFn: async () => {
      const result = await apiTimeTracking.getTimesheets(filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  employeeEntries: (employeeId: number, filter: ApiTimeEntryFilter) => ({
    queryKey: [employeeId, filter],
    queryFn: async () => {
      const result = await apiTimeTracking.getEntriesForEmployee(employeeId, filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useTimeTrackingStatus = (enabled: boolean = true) => {
  return useQuery({
    ...timeTrackingKeys.status,
    enabled,
    staleTime: 30 * 1000,
  });
};

export const useClockIn = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (comment: string | null) => {
      const result = await apiTimeTracking.clockIn(comment);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onError: (error: Error) => {
      onError(error);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: timeTrackingKeys.status.queryKey });
    },
  });
};

export const useClockOut = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (comment: string | null) => {
      const result = await apiTimeTracking.clockOut(comment);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onError: (error: Error) => {
      onError(error);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: timeTrackingKeys.status.queryKey });
    },
  });
};

export const useTimesheets = (filter: ApiTimesheetFilter, enabled = true) => {
  return useQuery({
    ...timeTrackingKeys.timesheets(filter),
    enabled,
    staleTime: 30 * 1000,
  });
};

export const useEmployeeTimeEntries = (
  employeeId: number | null,
  filter: ApiTimeEntryFilter,
  enabled = true
) => {
  return useQuery({
    ...timeTrackingKeys.employeeEntries(employeeId ?? 0, filter),
    enabled: enabled && employeeId !== null,
    staleTime: 10 * 1000,
  });
};

export const useCreateTimeEntry = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      employeeId,
      data,
    }: {
      employeeId: number;
      data: ApiTimeEntrySaveInput;
    }) => {
      const result = await apiTimeTracking.createEntry(employeeId, data);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeTrackingKeys._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useUpdateTimeEntry = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ entryId, data }: { entryId: number; data: ApiTimeEntrySaveInput }) => {
      const result = await apiTimeTracking.updateEntry(entryId, data);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeTrackingKeys._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useDeleteTimeEntry = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (entryId: number) => {
      const result = await apiTimeTracking.deleteEntry(entryId);
      if (result.error) throw new ApiErrorException(result.error);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeTrackingKeys._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
